import { EMAIL_CONTENT_SOURCE } from '@/emails/constants';
import { getContent } from '@/guides/helpers/common.util';
import { createGuide } from '@/guides/helpers/guide-editor.util.js';
import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { isMobileApp } from '@/utils/apps';
import { formatRatePercent } from '@/utils/formatters';
import { i18n_t as $t } from '@/utils/i18n';
import generateUniqueId from '@/utils/makeid';
import { canDeleteEmail } from '@/emails/emails-permissions.utils';
import { BuildingBlockEmails, StepCopy } from '@pendo/services/BuildingBlocks';
import { EMAIL, MESSAGE_TYPES } from '@pendo/services/Constants';
import {
    formatEmailTemplates,
    createEmailSanitizeConfig,
    stripEmailAnalyticsAttributesFromHtml,
} from '@pendo/services/EmailUtils';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import sanitizeHtml from 'sanitize-html';

const { EMAIL: EMAIL_MESSAGE_TYPE } = MESSAGE_TYPES;
const { EMAIL_TYPES } = EMAIL;

export function getClonedEmailDisplayName (emailName) {
    return $t('emailConstsUtils.Copy_of__emailName__', { emailName: emailName || '' });
}

export function isScheduled (email) {
    return email?.schedule?.startTime > Date.now();
}

export async function addStep (guide, newGuide) {
    const { data } = await generateUniqueId();

    const step = guide.steps[0];

    if (!step) return newGuide;

    const { type, advanceMethod, rank, templateId, content, layout } = step;

    const { otherProps: { attributes }, buildingBlocks, dom } = StepCopy.copyStep(step, newGuide.id, `pendo-g-${guide.id}`, {}, true, {
        isGuideCopy: true,
    });
    const { htmlTemplate, textTemplate } = BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks);

    newGuide.steps.push({
        advanceMethod,
        attributes,
        buildingBlocks: JSON.stringify(buildingBlocks),
        content,
        dom: JSON.stringify(dom),
        guideId: newGuide.id,
        htmlTemplate,
        id: data.id,
        layout,
        rank,
        templateId,
        textTemplate,
        type,
    });
}

export async function buildClonedEmailGuide (guide, app) {
    const isMobile = isMobileApp(app.platform);

    if (!guide.steps[0].buildingBlocks) {
        try {
            guide = await getContent(guide);
        } catch (err) {
            throw err;
        }
    }

    const newGuide = await createGuide({
        guideType: 'building-block',
        app,
        skipAddStep: true,
        skipPriority: true,
    });

    delete newGuide.name;

    newGuide.messageTarget = EMAIL_MESSAGE_TYPE;
    newGuide.attributes.isEmail = true;
    newGuide.attributes.isEmailTransactional = get(guide, 'attributes.isEmailTransactional', false);

    if (isMobile) {
        newGuide.minSdkVersion = guide.minSdkVersion;
    }

    await addStep(guide, newGuide);

    return newGuide;
}

export function isPendoEmail ({ provider }) {
    return [PROVIDER_TYPES.AWS].includes(provider);
}

export function isIntegratedEmail ({ provider }) {
    return [PROVIDER_TYPES.HUBSPOT, PROVIDER_TYPES.MARKETO, PROVIDER_TYPES.ELOQUA].includes(provider);
}

export function isHtmlEmail (email) {
    return email?.contentSource === EMAIL_CONTENT_SOURCE.HTML;
}

export function getComputedStatus (status) {
    return status === 'public' ? 'active' : status;
}

export function buildStatusOption ({ status, schedule }) {
    if (status === 'public') {
        const hasFutureTime = isScheduled({ schedule });

        if (hasFutureTime) {
            return {
                label: $t('common.Scheduled'),
                state: 'scheduled',
            };
        }
    }

    return {
        label: capitalize(getComputedStatus(status)),
        state: status,
    };
}

export function buildEmailSettings (email) {
    if (!email?.id) {
        return null;
    }

    const { id, name, status, schedule } = email;

    return {
        id,
        name,
        status: buildStatusOption({ status, schedule }).state,
    };
}

export function isJourneyEmail (email) {
    return !!email.attributes?.journeyId;
}

export function isEmailDeletable (email) {
    return !isJourneyEmail(email) && !!canDeleteEmail({ appId: email.appId, email });
}

export function fitEmailStatus (status) {
    const statusMap = {
        review: 'pending-review',
        sent: 'archived',
    };

    return statusMap[status] || status;
}

export function isEmailEntity (guide) {
    return guide.kind === 'PinpointCampaign';
}

export function buildTemplateFromBBString (bbString, languageCode) {
    try {
        const buildingBlocks = JSON.parse(bbString);

        const { htmlTemplate, textTemplate } = BuildingBlockEmails.buildingBlocksToHtml(buildingBlocks, {
            languageCode,
        });

        return formatEmailTemplates(htmlTemplate, textTemplate, true);
    } catch {
        return {
            htmlTemplate: '',
            textTemplate: '',
        };
    }
}

/**
 * Determines the appropriate group for an email, replacing with journey's group if applicable
 * @param {Object} email - The email object
 * @param {Function} groupByEntityId - Function to get group by entity ID
 * @param {Array} journeys - Array of journey objects
 * @returns {Object|null} The group object to use for the email
 */
export function getEmailGroup (email, groupByEntityId, journeys = []) {
    // Start with the email's own group
    let group = groupByEntityId(email.id);

    // For emails managed in journeys, use the journey's group
    if (email.attributes?.journeyId) {
        const journey = journeys.find((j) => j.id === email.attributes.journeyId);
        if (journey && journey.group) {
            group = journey.group;
        } else {
            const journeyGroup = groupByEntityId(email.attributes.journeyId);
            if (journeyGroup) {
                group = journeyGroup;
            }
        }
    }

    return group;
}

export function getPercentage (value) {
    return value ? formatRatePercent(value) : '0%';
}

export function getNumber (value) {
    return value ? value.toString() : '0';
}

export function sanitizeUrl (url) {
    try {
        const parsedUrl = new URL(url);
        const safeProtocols = ['http:', 'https:'];

        if (safeProtocols.includes(parsedUrl.protocol)) {
            return url;
        }
    } catch {
        return '';
    }

    return '';
}

export function getUiElementTypeIcon (elementType) {
    return (
        {
            Button: 'rectangle',
            Image: 'image',
            Link: 'link',
        }[elementType] || 'rectangle'
    );
}

export function removeUrlProtocolAndSearch (url) {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname === '/' ? '' : parsedUrl.pathname;

    return `${parsedUrl.origin.replace(/^(https?):\/\//, '')}${pathname}`;
}

export function isNotLaunchedTransactionalEmail (emailId, emailType, isLaunched) {
    if (!emailId || isLaunched) {
        return false;
    }

    return emailType === EMAIL_TYPES.TRANSACTIONAL;
}

export function sanitizeEmailHtml (html, config = {}) {
    return sanitizeHtml(html, createEmailSanitizeConfig({
        transformColors: false,
        injectAnalyticsAttributes: false,
        ...config,
    })) || '';
}

export function prepareEmailHtmlForEditor (html) {
    return stripEmailAnalyticsAttributesFromHtml(sanitizeEmailHtml(html));
}
